import type { GeraetAngelegt, GeraetAnlegenAnfrage, GeraetVorhanden } from '../../shared/api.js';
import type { Geraeteklasse } from '../../shared/pruefpunkte.js';
import { api, ApiFehler, fehlerText, modus } from '../api.js';
import { h, haengeAn, knopf, meldung, setzeMeldung } from '../dom.js';
import { melde } from '../ergebnisse.js';
import { erkenneKlasse, erkennePlattform, istIos } from '../geraet.js';
import { navigiere, type Ansicht } from '../router.js';
import { holeStand, speichereAnmeldung } from '../sitzung.js';
import { lies } from '../speicher.js';
import { formatiereZeitpunkt } from '../zeit.js';

/** Nur Pfade dieser App als Rücksprung zulassen. */
function sichererRuecksprung(wert: string | null): string {
  return wert && /^\/[^/\\]/.test(wert) ? wert : '/';
}

export const einrichtung: Ansicht = async (ziel, _params, suche) => {
  const weiter = sichererRuecksprung(suche.get('weiter'));
  const vorschlagName = (await lies('name')) ?? '';
  const klasseVorschlag = (await lies('klasse')) ?? erkenneKlasse();
  const status = meldung();

  const code = h('input', { id: 'zugangscode', name: 'zugangscode', type: 'text', required: true, autocomplete: 'off', autocapitalize: 'none', spellcheck: 'false', 'aria-describedby': 'zugangscode-hilfe' });
  const name = h('input', { id: 'geraetename', name: 'geraetename', type: 'text', required: true, minlength: 2, maxlength: 60, autocomplete: 'off', value: vorschlagName, 'aria-describedby': 'geraetename-hilfe' });
  const klasse = (wert: Geraeteklasse, text: string) =>
    h('label', { class: 'auswahl' }, h('input', { type: 'radio', name: 'klasse', value: wert, checked: klasseVorschlag === wert }), text);

  const absenden = h('button', { type: 'submit', class: 'knopf knopf-primaer' }, 'Einrichten');
  const formular = h(
    'form',
    { class: 'formular', novalidate: true },
    h('div', { class: 'feld' }, h('label', { for: 'zugangscode' }, 'Zugangscode'), code, h('p', { id: 'zugangscode-hilfe', class: 'hilfe' }, 'Steht in der Testanleitung.')),
    h(
      'div',
      { class: 'feld' },
      h('label', { for: 'geraetename' }, 'Name des Geräts'),
      name,
      h('p', { id: 'geraetename-hilfe', class: 'hilfe' }, 'z. B. „iPhone SE Klaus“. Für App und Browser auf demselben Gerät denselben Namen verwenden, dann werden die Ergebnisse zusammengeführt.'),
    ),
    h('fieldset', { class: 'feld' }, h('legend', {}, 'Art des Geräts'), klasse('handy', 'Handy oder Tablet'), klasse('pc', 'PC oder Laptop')),
    absenden,
    status,
  );
  const auswahlBereich = h('div', {});

  /** offlineVorher: Das weitergeführte Gerät hatte Offline-Daten, die jetzt fehlen. */
  async function sende(zusatz: Partial<GeraetAnlegenAnfrage> = {}, offlineVorher = false): Promise<void> {
    const gewaehlt = (formular.querySelector('input[name="klasse"]:checked') as HTMLInputElement | null)?.value as Geraeteklasse | undefined;
    const anfrage: GeraetAnlegenAnfrage = {
      zugangscode: code.value.trim(),
      name: name.value.trim(),
      modus: modus(),
      klasse: gewaehlt ?? klasseVorschlag,
      plattform: await erkennePlattform(),
      ...zusatz,
    };
    code.toggleAttribute('aria-invalid', !anfrage.zugangscode);
    name.toggleAttribute('aria-invalid', anfrage.name.length < 2);
    if (!anfrage.zugangscode || anfrage.name.length < 2) {
      setzeMeldung(status, 'Bitte Zugangscode und einen Namen mit mindestens 2 Zeichen eingeben.', 'fehler');
      (anfrage.zugangscode ? name : code).focus();
      return;
    }
    setzeMeldung(status, 'Wird eingerichtet …');
    absenden.disabled = true;
    absenden.setAttribute('aria-busy', 'true');
    try {
      const antwort = await api<GeraetAngelegt>('/api/geraete', { json: anfrage, modus: null });
      await speichereAnmeldung(antwort, anfrage.klasse);
      if (antwort.uebernommen && offlineVorher) {
        // Dieses Gerät hatte Offline-Daten, der lokale Speicher war aber leer.
        await melde('offline-daten', 'fehler', 'Lokaler Speicher war leer: Offline-Daten und Anmeldung fehlten (App neu installiert oder Daten vom System gelöscht).', {
          grund: 'speicher-leer',
        });
      }
      await holeStand().catch(() => {});
      navigiere(weiter, true);
    } catch (fehler) {
      absenden.disabled = false;
      absenden.removeAttribute('aria-busy');
      if (fehler instanceof ApiFehler && fehler.status === 401) code.setAttribute('aria-invalid', 'true');
      if (fehler instanceof ApiFehler && fehler.status === 409) {
        zeigeVorhandene((fehler.daten as GeraetVorhanden).vorhanden);
        setzeMeldung(status, 'Diesen Namen gibt es schon.', 'info');
        return;
      }
      setzeMeldung(status, fehlerText(fehler), 'fehler');
    }
  }

  function zeigeVorhandene(vorhanden: GeraetVorhanden['vorhanden']): void {
    auswahlBereich.replaceChildren(
      h(
        'section',
        { class: 'karte hinweis', 'aria-labelledby': 'vorhanden-titel' },
        h('h2', { id: 'vorhanden-titel' }, `„${name.value.trim()}“ gibt es schon`),
        h('p', {}, 'Ist das dieses Gerät und war sein Speicher leer (App neu installiert oder Daten gelöscht), dann weiterführen. Sonst einen neuen Eintrag anlegen.'),
        ...vorhanden.map((v) =>
          h(
            'div',
            { class: 'zeilenknopf' },
            h('p', {}, `Zuletzt gesehen ${formatiereZeitpunkt(v.zuletztGesehen)}`, v.offlineDatenAngelegt ? ', mit Offline-Daten' : ''),
            knopf('Dieses Gerät weiterführen', () => sende({ uebernehmen: v.id }, v.offlineDatenAngelegt)),
          ),
        ),
        knopf('Neues Gerät anlegen', () => sende({ trotzdemNeu: true }), 'sekundaer'),
      ),
    );
    auswahlBereich.querySelector('h2')?.setAttribute('tabindex', '-1');
    (auswahlBereich.querySelector('h2') as HTMLElement | null)?.focus();
  }

  formular.addEventListener('submit', (e) => {
    e.preventDefault();
    void sende();
  });

  const iosImBrowser = istIos() && modus() === 'browser' && !suche.has('weiter');
  haengeAn(ziel, [
    h('h1', {}, 'Gerät einrichten'),
    h('p', {}, 'Für den Gerätetest wird jedes Gerät einmal mit dem Zugangscode angemeldet. Die Ergebnisse speichert der Server je Gerät.'),
    iosImBrowser &&
      h(
        'section',
        { class: 'karte hinweis' },
        h('h2', {}, 'Am iPhone zuerst installieren'),
        h('p', {}, 'Safari und die installierte App haben getrennte Speicher. Deshalb erst über Teilen → „Zum Home-Bildschirm“ installieren und dann in der App einrichten.'),
      ),
    formular,
    auswahlBereich,
    h(
      'p',
      { class: 'leise' },
      'Nur auswerten oder Aufkleber drucken? Dafür muss dieses Gerät nicht eingerichtet werden: ',
      h('a', { href: '/auswertung' }, 'Auswertung'),
      ' · ',
      h('a', { href: '/druck' }, 'QR-Aufkleber'),
      '.',
    ),
  ]);
};
